
import { supabase } from "@/integrations/supabase/client";
import { MFOSubTask } from "@/types/kanban";

export const fetchSubtasksForMFO = async (mfoId: string): Promise<MFOSubTask[]> => {
  try {
    const { data, error } = await supabase
      .from('mfo_subtasks')
      .select('*')
      .eq('mfo_id', mfoId);
      
    if (error) throw error;
    
    return data.map(st => ({
      id: st.id,
      text: st.text,
      completed: st.completed
    }));
  } catch (error) {
    console.error("Error fetching subtasks:", error);
    return [];
  }
};

export const createDefaultSubtasksForMFO = async (mfoId: string): Promise<void> => {
  try {
    const defaultSubtasks = [
      { text: "Create project documentation", completed: false },
      { text: "Conduct stakeholder meetings", completed: false },
      { text: "Finalize implementation plan", completed: false },
      { text: "Submit progress report", completed: false },
      { text: "Complete final deliverables", completed: false }
    ];
    
    const subtasksToInsert = defaultSubtasks.map(st => ({
      mfo_id: mfoId,
      text: st.text,
      completed: st.completed
    }));
    
    const { error } = await supabase
      .from('mfo_subtasks')
      .insert(subtasksToInsert);
      
    if (error) throw error;
  } catch (error) {
    console.error("Error creating default subtasks:", error);
  }
};

export const updateSubtaskStatus = async (
  subtaskId: string, 
  completed: boolean
): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('mfo_subtasks')
      .update({ completed })
      .eq('id', subtaskId);
      
    if (error) throw error;
    return true;
  } catch (error) {
    console.error("Error updating subtask:", error);
    return false;
  }
};

export const recalculateAndUpdateMFOProgress = async (mfoId: string): Promise<number> => {
  try {
    // Fetch all subtasks for the MFO
    const { data: subtasks, error } = await supabase
      .from('mfo_subtasks')
      .select('completed')
      .eq('mfo_id', mfoId);
      
    if (error) throw error;
    
    if (!subtasks || subtasks.length === 0) return 0;
    
    // Calculate progress based on completed subtasks
    const completedCount = subtasks.filter(st => st.completed).length;
    const progress = Math.round((completedCount / subtasks.length) * 100);
    
    // Update MFO progress
    const { error: updateError } = await supabase
      .from('mfo_items')
      .update({ progress })
      .eq('id', mfoId);
      
    if (updateError) throw updateError;
    
    // Also update any kanban cards linked to this MFO
    const { error: cardUpdateError } = await supabase
      .from('kanban_cards')
      .update({ progress })
      .eq('mfo_id', mfoId);
      
    if (cardUpdateError) {
      console.error("Error updating kanban card progress:", cardUpdateError);
    }
    
    return progress;
  } catch (error) {
    console.error("Error recalculating progress:", error);
    return -1;
  }
};
